import { CheckCheck, Send } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { DateCell } from '../../../../components/table/DateCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { RouteDocumentModal } from '../components/RouteDocumentModal';
import { inboxApi } from '../routingApi';
import { INBOX_PATH, inboxReceiveDisabledReason, inboxRouteDisabledReason, isInboxRecipient } from '../routingRules';
import { INBOX_AUDIT_TYPES, type DispatchedDocumentListItem } from '../routingTypes';
import { useRoutingDesk } from '../useRoutingDesk';

/**
 * Inbox — every document Dispatching sent to a unit of this organization, from the side that
 * receives it.
 *
 * **It owns no record.** An item IS the dispatch, read from the other end: the sender records and
 * corrects it in Dispatching; here it is RECEIVED (its dispatch line acknowledged, on
 * `document-inbox.receive`) and then ROUTED on, unit to unit, on `document-inbox.route`. Nothing
 * here creates, edits or deletes, so there is no Create and no row Edit or Delete.
 *
 * **It lands on In transit** — a desk opens its Inbox to see what is waiting, so the resting
 * question is narrower than the contents; the `All` tab is still there. The routing axis is the
 * folder nav. A dispatch its sender DELETED is hidden at rest and shown with Show deleted, like
 * every list — and takes no write.
 *
 * **Receive is the row's quick action** while it is waiting. It writes outside a modal, so it
 * confirms with a tick.
 */
export function InboxIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [receiving, setReceiving] = useState<DispatchedDocumentListItem | null>(null);
  const [routing, setRouting] = useState<DispatchedDocumentListItem | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  // Where this reader sits — Receive is offered only on rows sent to their own unit.
  const { desk, prerequisite: deskPrerequisite } = useRoutingDesk(inboxApi.desk);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      inboxApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          routing: query.facets.routing?.join(','),
          recipient: query.facets.recipient?.join(','),
          holder: query.facets.holder?.join(','),
          documentType: query.facets.documentType?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (row: DispatchedDocumentListItem): MenuItem[] => [
      // Receiving is the RECIPIENT unit's act — a row sent elsewhere offers no Receive at all.
      ...(isInboxRecipient(row, desk)
        ? [
            {
              key: `receive-${row.id}`,
              label: 'Receive',
              icon: <CheckCheck className="h-4 w-4" aria-hidden="true" />,
              permission: 'document-inbox.receive',
              disabledReason: inboxReceiveDisabledReason(row),
              onClick: () => setReceiving(row),
            },
          ]
        : []),
      {
        key: `route-${row.id}`,
        label: 'Route',
        icon: <Send className="h-4 w-4" aria-hidden="true" />,
        permission: 'document-inbox.route',
        disabledReason: inboxRouteDisabledReason(row, desk),
        onClick: () => setRouting(row),
      },
    ],
    [desk],
  );

  // PROMOTED, never duplicated — only on a row still waiting for THIS reader's unit.
  const quickActions = useCallback(
    (row: DispatchedDocumentListItem) =>
      row.isActive && row.inboxReceivedAt == null ? rowActions(row).filter((item) => item.key.startsWith('receive')) : [],
    [rowActions],
  );

  async function receive() {
    if (!receiving) return;

    setRunning(true);
    setError(null);

    try {
      await inboxApi.receive(receiving.id);
      toast.success(`Received ${receiving.trackingNumber} at ${receiving.recipientOrgUnitName ?? 'the unit'}.`);
      setReceiving(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not receive it.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<DispatchedDocumentListItem>
        module="document-inbox"
        moduleNoun="inbox items"
        breadcrumb={['Document Management', 'Routing', 'Inbox']}
        columns={COLUMNS}
        facets={[
          { key: 'routing', label: 'Routing' },
          { key: 'recipient', label: 'To' },
          { key: 'holder', label: 'With' },
          { key: 'documentType', label: 'Document Type' },
          { key: 'status', label: 'Status' },
        ]}
        folderNavFacet="routing"
        initialFacets={{ routing: ['InTransit'] }}
        auditEntityTypes={INBOX_AUDIT_TYPES}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by subject, tracking no., unit, waybill or type"
        emptyMessage="Nothing has been dispatched to a unit yet. A dispatch addressed to a unit of this organization arrives here."
        prerequisites={[deskPrerequisite]}
        onRowOpen={(row) => navigate(`${INBOX_PATH}/${row.id}`)}
        openPermission="document-inbox.read"
        rowActions={rowActions}
        rowQuickActions={quickActions}
        otherInfo={(row) => row}
      />

      {receiving ? (
        <ConfirmDialog
          open
          onClose={() => {
            setReceiving(null);
            setError(null);
          }}
          onConfirm={() => void receive()}
          title="Receive document"
          requireConfirm
          loading={running}
          id="inbox-receive-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                {receiving.recipientOrgUnitName ?? 'The unit'} takes {receiving.trackingNumber} — {receiving.subject} in. It
                is that unit&apos;s from now on, and {receiving.originatingOrgUnitName} can no longer delete it or change where
                it was sent.
              </p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      {routing ? (
        <RouteDocumentModal
          open
          api={inboxApi}
          document={routing}
          onClose={() => setRouting(null)}
          onSaved={() => {
            setRouting(null);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<DispatchedDocumentListItem>[] = [
  {
    key: 'subject',
    header: 'Document',
    width: 340,
    flex: true,
    sortable: true,
    render: (row) => <TextCell code={row.trackingNumber} value={row.subject} clamp />,
  },
  {
    key: 'documentTypeName',
    header: 'Type',
    width: 150,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={row.documentTypeName} />,
  },
  {
    key: 'originatingOrgUnitName',
    header: 'From Unit',
    width: 200,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={row.originatingOrgUnitName} clamp />,
  },
  {
    key: 'recipient',
    header: 'To',
    width: 200,
    sortable: true,
    render: (row) => <TextCell value={row.recipientOrgUnitName} clamp />,
  },
  {
    key: 'dispatchedAt',
    header: 'Dispatched',
    width: 170,
    sortable: true,
    summary: true,
    render: (row) => <DateCell value={row.dispatchedAt} />,
  },
  {
    // Blank while it is waiting — nobody has taken it in.
    key: 'inboxReceivedAt',
    header: 'Received',
    width: 170,
    render: (row) => (row.inboxReceivedAt != null ? <DateCell value={row.inboxReceivedAt} /> : <TextCell value={null} />),
  },
  {
    // A SET — several units once it has been routed on to more than one; one line, joined.
    key: 'holders',
    header: 'With',
    width: 220,
    render: (row) => <TextCell value={row.holders.length > 0 ? row.holders.map((h) => h.name).join(', ') : null} clamp />,
  },
  {
    key: 'attachmentCount',
    header: 'Files',
    width: 80,
    align: 'right',
    render: (row) => <TextCell value={String(row.attachmentCount)} mono numeric />,
  },
  {
    key: 'routing',
    header: 'Routing',
    width: 130,
    stage: true,
    render: (row) => <StatusText tone="inactive">{row.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
